import {
    buildDurationOptions,
    eventDurationOptionsMinutes,
    EventId,
    formatDuration,
    TaskTypeId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { Editor, PlacementEditor } from "components/editor/editor";
import { trpc } from "components/rpc/trpc";
import { timezoneOptionsIncluding } from "components/shared/timezone-options";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { TimePicker } from "components/ui/date-time-picker/time-picker";
import { InlineBanner } from "components/ui/inline-banner";
import { InlineTextField } from "components/ui/inline-field";
import { Select } from "components/ui/select";
import { isSelectProHeaderOption } from "helpers/select-defs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStatesArray } from "util/states";
import { editorDescriptionOrNull, eventDescriptionHtml } from "./event-description";
import { eventAddressSingleLine } from "./event-format";
import { instantToLocalParts, localPartsToInstant } from "./event-time";

const EVENT_TIME_GRANULARITY_MINUTES = 5;

export interface EditableEventDetails {
    id: EventId;
    activityTypeId?: TaskTypeId | null;
    title: string;
    description?: string | null;
    startsAt: string;
    timeZone: string;
    eventDuration: number;
    location?: string | null;
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
}

export const hasEventDetails = (event: EditableEventDetails): boolean =>
    !!(event.location || event.street || event.description);

const orNull = (value: string): string | null =>
    value.trim() === "" ? null : value.trim();

interface EventAddressPatch {
    location?: string | null;
    street?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
}

interface EventPatch {
    activityTypeId?: TaskTypeId | null;
    title?: string;
    description?: string | null;
    schedule?: { startsAt?: Date; timeZone?: string; eventDuration?: number };
    address?: EventAddressPatch;
}

interface EventDetailsBlockProps {
    event: EditableEventDetails;
    editing: boolean;
    onChanged: () => Promise<unknown>;
}

export const EventDetailsBlock: React.FC<EventDetailsBlockProps> = props => {
    const { event, editing, onChanged } = props;
    return (
        <div className="space-y-2">
            {editing ? (
                <EventInlineFields event={event} onChanged={onChanged} />
            ) : (
                <EventDetailsRead event={event} />
            )}
        </div>
    );
};

const EventDetailsRead: React.FC<{ event: EditableEventDetails }> = props => {
    const { event } = props;
    const address = eventAddressSingleLine(event);
    return (
        <div className="max-w-3xl text-sm">
            {event.location ? <p className="text-gray-900">{event.location}</p> : null}
            {address ? (
                <p className="mt-1">
                    <a
                        className="text-brand-600 hover:text-brand-700"
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            address,
                        )}`}
                        target="_blank"
                        rel="noreferrer">
                        {address}
                    </a>
                </p>
            ) : null}
            {event.description ? (
                <div
                    className="unreset mt-2 text-gray-900"
                    dangerouslySetInnerHTML={{
                        __html: eventDescriptionHtml(event.description),
                    }}
                />
            ) : null}
            {!hasEventDetails(event) ? (
                <p className="text-gray-500">No location or description yet.</p>
            ) : null}
        </div>
    );
};

interface StateSelectProps {
    value: string;
    onCommit: (value: string) => void;
}

const StateSelect: React.FC<StateSelectProps> = props => {
    const { value, onCommit } = props;
    const groups = useMemo(() => getStatesArray().filter(isSelectProHeaderOption), []);
    const known = useMemo(
        () =>
            new Set(
                groups.flatMap(group => group.groupOptions.map(option => option.value)),
            ),
        [groups],
    );
    return (
        <Select label="State" value={value} onChange={e => onCommit(e.target.value)}>
            <option value=""></option>
            {value !== "" && !known.has(value) ? (
                <option value={value}>{value}</option>
            ) : null}
            {groups.map(group => (
                <optgroup key={group.header} label={group.header}>
                    {group.groupOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </optgroup>
            ))}
        </Select>
    );
};

interface EventInlineFieldsProps {
    event: EditableEventDetails;
    onChanged: () => Promise<unknown>;
}

const EventInlineFields: React.FC<EventInlineFieldsProps> = props => {
    const { event, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const options = useActivityTypeOptions();
    const eventTypes = useMemo(
        () =>
            options.filter(
                option =>
                    option.category === "event" &&
                    (!option.isArchived || option.id === event.activityTypeId),
            ),
        [options, event.activityTypeId],
    );
    const parts = instantToLocalParts(event.startsAt, event.timeZone);

    const [validationError, setValidationError] = useState<string | null>(null);
    const mutation = useMutation({
        ...trpcClient.event.update.mutationOptions(),
        onSuccess: async () => {
            setValidationError(null);
            await onChanged();
        },
    });

    const patch = useCallback(
        (fields: EventPatch) => {
            setValidationError(null);
            mutation.mutate({ id: event.id, ...fields });
        },
        [mutation, event.id],
    );
    const startsAtRef = useRef(new Date(event.startsAt));
    useEffect(() => {
        startsAtRef.current = new Date(event.startsAt);
    }, [event.startsAt]);
    const timeZoneRef = useRef(event.timeZone);
    useEffect(() => {
        timeZoneRef.current = event.timeZone;
    }, [event.timeZone]);

    const commitTitle = useCallback(
        (value: string) => {
            const trimmed = value.trim();
            if (trimmed === "") {
                setValidationError("Give the event a title.");
                return;
            }
            patch({ title: trimmed });
        },
        [patch],
    );
    const descriptionEditorRef = useRef<PlacementEditor>(null);
    const seededDescriptionRef = useRef(eventDescriptionHtml(event.description ?? ""));
    useEffect(() => {
        seededDescriptionRef.current = eventDescriptionHtml(event.description ?? "");
    }, [event.description]);
    const commitDescription = useCallback(() => {
        const content = descriptionEditorRef.current?.getContent() ?? "";
        if (content === seededDescriptionRef.current) {
            return;
        }
        const value = editorDescriptionOrNull(content);
        if (value === (event.description ?? null)) {
            return;
        }
        patch({ description: value });
    }, [patch, event.description]);
    const commitType = useCallback(
        (value: string) =>
            patch({ activityTypeId: value === "" ? null : (value as TaskTypeId) }),
        [patch],
    );
    const commitStartsAt = useCallback(
        (next: Date) => {
            startsAtRef.current = next;
            patch({ schedule: { startsAt: next } });
        },
        [patch],
    );
    const commitDate = useCallback(
        (value: string) => {
            const current = instantToLocalParts(
                startsAtRef.current.toISOString(),
                timeZoneRef.current,
            );
            commitStartsAt(localPartsToInstant(value, current.time, timeZoneRef.current));
        },
        [commitStartsAt],
    );
    const commitTime = useCallback(
        (value: string) => {
            const current = instantToLocalParts(
                startsAtRef.current.toISOString(),
                timeZoneRef.current,
            );
            commitStartsAt(localPartsToInstant(current.date, value, timeZoneRef.current));
        },
        [commitStartsAt],
    );
    const commitTimeZone = useCallback(
        (zone: string) => {
            const current = instantToLocalParts(
                startsAtRef.current.toISOString(),
                timeZoneRef.current,
            );
            const next = localPartsToInstant(current.date, current.time, zone);
            startsAtRef.current = next;
            timeZoneRef.current = zone;
            patch({ schedule: { startsAt: next, timeZone: zone } });
        },
        [patch],
    );
    const commitDuration = useCallback(
        (value: string) => {
            patch({ schedule: { eventDuration: parseInt(value, 10) } });
        },
        [patch],
    );
    const commitAddressField = useCallback(
        (field: "location" | "street" | "city" | "state" | "zip", value: string) =>
            patch({ address: { [field]: orNull(value) } }),
        [patch],
    );

    return (
        <div className="max-w-3xl space-y-4">
            {validationError ? (
                <InlineBanner type="error">{validationError}</InlineBanner>
            ) : null}
            {mutation.isError ? (
                <InlineBanner type="error">
                    Could not save that change. Please try again.
                </InlineBanner>
            ) : null}
            <div className="grid grid-cols-1 items-start gap-x-4 gap-y-4 lg:grid-cols-2">
                <div className="lg:col-span-2">
                    <InlineTextField
                        label="Title"
                        initial={event.title}
                        onCommit={commitTitle}
                    />
                </div>
                <Select
                    label="Event type"
                    value={event.activityTypeId ?? ""}
                    onChange={e => commitType(e.target.value)}>
                    <option value="">No type</option>
                    {eventTypes.map(option => (
                        <option key={option.id} value={option.id}>
                            {option.name}
                        </option>
                    ))}
                </Select>
                <Select
                    label="Time zone"
                    value={event.timeZone}
                    onChange={e => commitTimeZone(e.target.value)}>
                    {timezoneOptionsIncluding(event.timeZone).map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-2">
                    <DatePicker
                        label="Date"
                        value={parts.date}
                        onChange={value => {
                            if (value) {
                                commitDate(value);
                            }
                        }}
                        onOpenChange={() => undefined}
                        disabled={false}
                        required={true}
                        textSize="base"
                        minValue={null}
                        maxValue={null}
                        visibleYears={20}
                    />
                    <TimePicker
                        label="Start time"
                        value={parts.time}
                        onChange={value => {
                            if (value) {
                                commitTime(value);
                            }
                        }}
                        disabled={false}
                        nativeStepMinutes={EVENT_TIME_GRANULARITY_MINUTES}
                    />
                    <Select
                        label="Duration"
                        value={String(event.eventDuration)}
                        onChange={e => commitDuration(e.target.value)}>
                        {buildDurationOptions(
                            eventDurationOptionsMinutes,
                            event.eventDuration,
                        ).map(minutes => (
                            <option key={minutes} value={String(minutes)}>
                                {formatDuration(minutes)}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="lg:col-span-2">
                    <InlineTextField
                        label="Location name"
                        initial={event.location ?? ""}
                        onCommit={value => commitAddressField("location", value)}
                    />
                </div>
                <div className="lg:col-span-2">
                    <InlineTextField
                        label="Address"
                        initial={event.street ?? ""}
                        onCommit={value => commitAddressField("street", value)}
                    />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:col-span-2">
                    <InlineTextField
                        label="City"
                        initial={event.city ?? ""}
                        onCommit={value => commitAddressField("city", value)}
                    />
                    <StateSelect
                        value={event.state ?? ""}
                        onCommit={value => commitAddressField("state", value)}
                    />
                    <InlineTextField
                        label="Zip"
                        initial={event.zip ?? ""}
                        onCommit={value => commitAddressField("zip", value)}
                    />
                </div>
                <div className="lg:col-span-2">
                    <p className="mb-1 block">Description</p>
                    <Editor
                        ref={descriptionEditorRef}
                        initialContent={
                            event.description
                                ? eventDescriptionHtml(event.description)
                                : ""
                        }
                        onBlur={commitDescription}
                    />
                </div>
            </div>
        </div>
    );
};
